<template>
  <nav class="main-nav">
    <div class="nav-container">
      <div class="nav-brand">
        <router-link to="/" class="brand-link">
          <img src="img/logo.svg" :alt="logoAlt" class="logo" />
        </router-link>
      </div>
      
      <div class="nav-links">
        <template v-if="!isPortrait">
          <template
            v-for="(link, index) in filteredLinks"
            :key="index"
          >
            <!-- Router link -->
            <router-link
              v-if="link.to"
              :to="link.to"
              :class="['nav-link', 'router-link', link.class, { 'router-link-active': $route.path === link.to }]"
              :title="link.title || ''"
              :target="link.target || ''"
              :rel="link.rel || ''"
              v-html="link.label"
            ></router-link>

            <!-- External link -->
            <a
              v-else-if="link.href"
              :href="link.href"
              :class="['nav-link', 'external-link', link.class]"
              :title="link.title"
              :target="link.target || ''"
              :rel="link.rel || ''"
              @click="link.handleClick ? link.handleClick() : null"
              v-html="link.label"
            ></a>

            <!-- Dropdown -->
            <div v-if="link.children" class="nav-dropdown">
              <span class="nav-link dropdown-label" v-html="link.label"></span>
              <div class="dropdown-menu">
                <template v-for="(child, cidx) in link.children" :key="cidx">
                  <router-link
                    v-if="child.to"
                    :to="child.to"
                    :class="['nav-link', 'router-link', child.class, { 'router-link-active': $route.path === child.to }]"
                    :title="child.title || ''"
                    :target="child.target || ''"
                    :rel="child.rel || ''"
                    v-html="child.label"
                  ></router-link>
                  <a
                    v-else
                    :href="child.href"
                    :class="['nav-link', child.class]"
                    :title="child.title"
                    :target="child.target || ''"
                    :rel="child.rel || ''"
                    v-html="child.label"
                  ></a>
                </template>
              </div>
            </div>
          </template>
        </template>

        <template v-else>
          <div class="nav-links-stacked">
            <template
              v-for="(link, index) in filteredLinks"
              :key="index"
            >
              <!-- Children -->
              <template v-if="link.children">
                <span class="nav-link dropdown-label" v-html="link.label"></span>
                <div class="dropdown-menu" style="display: flex; position: static; box-shadow: none;">
                  <template v-for="(child, cidx) in link.children" :key="cidx">
                    <router-link
                      v-if="child.to"
                      :to="child.to"
                      class="nav-link router-link"
                      :title="child.title || ''"
                      v-html="child.label"
                    ></router-link>
                    <a
                      v-else
                      :href="child.href"
                      class="nav-link external-link"
                      :title="child.title"
                      :target="child.target || ''"
                      :rel="child.rel || ''"
                      v-html="child.label"
                    ></a>
                  </template>
                </div>
              </template>

              <!-- Link with click handler -->
              <a
                v-else-if="link.handleClick"
                href="#"
                class="nav-link"
                @click.prevent.stop="link.handleClick && link.handleClick()"
                v-html="link.label"
              ></a>

              <!-- Router link -->
              <router-link
                v-else-if="link.to"
                :to="link.to"
                class="nav-link router-link"
                :title="link.title || ''"
                v-html="link.label"
              ></router-link>

              <!-- External link -->
              <a
                v-else-if="link.href"
                :href="link.href"
                class="nav-link external-link"
                :title="link.title"
                :target="link.target || ''"
                :rel="link.rel || ''"
                v-html="link.label"
              ></a>

              <!-- Label only -->
              <span v-else class="nav-link dropdown-label" v-html="link.label"></span>
            </template>
          </div>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useStore } from '../services/store.service';
import { useAuthService } from '../services/auth.service';
import { User } from '../types/api.types';

interface NavLink {
  label: string;
  to?: string;
  href?: string;
  class?: string;
  title?: string;
  target?: string;
  rel?: string;
  handleClick?: () => void;
  roles: string[]; // Allowed roles: 'admin', 'moderator', 'user', 'hideIfAuth', empty [] for public
  children?: NavLink[];
}

const apiBaseUrl = process.env.VUE_APP_API_URL;
const store = useStore();
const logoAlt = store.readConfig<string>('SITE_NAME') || 'SITE_NAME';
const authService = useAuthService();

const isAuthenticated = computed(() => {
  return store.isAuthenticated() || false;
});

const user = computed(() => {
  const data = store.getData<User>('user');
  if (!isAuthenticated.value) {
    return { name: 'Profile', role: '' };
  }
  return data || { name: 'Profile', role: '' } as Pick<User, 'name' | 'role'>;
});

// Logout handler
const handleLogout = () => {
  authService.logout();
};

const navLinks = ref<NavLink[]>([
  {
    label: '<i class="bi bi-house"></i> Home',
    to: '/',
    title: `Home`,
    roles: [],
  },
  {
    label: '<i class="bi bi-gear"></i> Dashboard',
    roles: ['admin', 'moderator'],
    children: [
      {
        label: '<i class="bi bi-database-gear"></i> System',
        to: '/system',
        title: `System`,
        roles: ['admin'],
      },
      {
        label: '<i class="bi bi-people-fill"></i> Users',
        to: '/users',
        title: `Users`,
        roles: ['admin', 'moderator'],
      },
      {
        label: '<i class="bi bi-emoji-smile"></i> Bootstrap Icons',
        href: 'https://icons.getbootstrap.com/',
        title: 'Bootstrap Icons',
        target: '_blank',
        rel: 'noreferrer',
        roles: ['user', 'moderator', 'admin'],
      },
      {
        label: 'API error',
        href: apiBaseUrl,
        title: `API URL: ${apiBaseUrl}`,
        class: 'api-test',
        rel: 'noreferrer',
        roles: [],
      },
      {
        label: 'API Test',
        href: `${apiBaseUrl}/test`,
        class: 'api-test',
        title: `API URL: ${apiBaseUrl}`,
        target: '_blank',
        rel: "noreferrer",
        roles: ['admin'],
      },
    ]
  },
  {
    label: '<i class="bi bi-box-arrow-in-right"></i> Login',
    to: '/login',
    title: 'Login to the system',
    roles: ['hideIfAuth'],
  },
  {
    label: `<i class="bi bi-person-circle"></i> ${user.value.name}`,
    to: '/profile',
    title: user.value.name,
    roles: ['user', 'moderator', 'admin'],
  },
  {
    label: '<i class="bi bi-box-arrow-right"></i>',
    href: '#',
    title: 'Logout',
    handleClick: handleLogout,
    rel: 'noopener noreferrer',
    roles: ['user', 'moderator', 'admin'],
  },
]);

// Filter visible links based on authentication status
const filteredLinks = computed(() => {
  const userRole = user.value.role || '';
  
  function isAllowed(link: NavLink): boolean {
    if (link.roles.includes('hideIfAuth')) {
      return link.roles.includes('hideIfAuth') && !isAuthenticated.value;
    }
    if (link.roles.length === 0) {
      return true;
    }
    return link.roles.includes(userRole);
  }

  return navLinks.value
    .filter(isAllowed)
    .map(link => {
      if (link.children) {
        return {
          ...link,
          children: link.children.filter(isAllowed)
        };
      }
      return link;
    });
});

const isPortrait = computed(() => store.getOrientation() === 'portrait');
</script>

<style scoped>
.main-nav {
  background-color: white;
  border-bottom: 1px solid var(--gray-200);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.main-nav .logo {
  height: 40px;
  width: auto;
  margin-right: 1rem;}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
}

.nav-brand .brand-link {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--nav-link-color);
  text-decoration: none;
  transition: color 0.2s;
}

.nav-brand .brand-link:hover {
  color: var(--nav-link-color);
}

.nav-links {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-link {
  color: var(--nav-link-color);
  text-decoration: none;
  font-weight: 500;
  padding: 0.25rem 0;
  border-bottom: 3px solid transparent;
  transition: all 0.2s;
}

.nav-link i {
  margin-right: 0.5rem;
  transition: all 0.2s;
}

.nav-link:hover {
  border-bottom-color: var(--nav-link-color);
}

.nav-link.router-link-active {
  color: var(--nav-link-color-active);
  border-bottom-color: var(--nav-link-color-active);
}

.nav-link.api-test {
  color: var(--nav-link-color-disabled);
}

.nav-dropdown {
  position: relative;
  display: inline-block;
}
.dropdown-label {
  cursor: pointer;
}
.dropdown-menu {
  display: none;
  position: absolute;
  background: white;
  min-width: 180px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  z-index: 10;
  flex-direction: column;
}
.nav-dropdown:hover .dropdown-menu {
  display: flex;
}

.dropdown-menu .nav-link {
  padding: 0.5rem 1rem;
}

.nav-links-stacked {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.is-portrait .nav-container {
  flex-direction: column;
  height: auto;
  padding: 1rem;
  gap: 1rem;
}

.is-portrait .nav-links {
  display: block;
  gap: 0;
}

.is-portrait .nav-link:not(:last-child),
.dropdown-menu .nav-link:not(:last-child) {
  background-image: linear-gradient(to right, var(--gray-100), var(--gray-300));
  background-size: 1px 1px;
  background-repeat: repeat-x;
  background-position: center bottom;
  transition: none;
}
.is-portrait .nav-links-stacked {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.is-portrait .dropdown-menu {
  position: static;
  display: flex !important;
  box-shadow: none;
}
</style>
